<script setup>
// One Stage canonical EmptyState primitive.
// Used wherever a list, table, panel, or page would otherwise show nothing.
// Composition: icon | illustration | title | description | actions
//
// Variants:
//   - default (page-level)
//   - compact (inline / table-level)
//   - bordered (dashed border around the surface)
//
// Replaces:
//   - components/widgets/EmptyState.vue (legacy)
//   - bespoke "no records" markup across surfaces

import { useSlots } from 'vue';
import Icon from '../icon/Icon.vue';

defineProps({
  /** Icon name (lucide / ph / ri). Ignored when #illustration slot is used. */
  icon: { type: [String, Object, Function], default: '' },
  /** Title (required for a meaningful empty state). */
  title: { type: String, default: '' },
  /** Description / next-step copy. */
  description: { type: String, default: '' },
  /** Visual variant. */
  variant: {
    type: String,
    default: 'default',
    validator: v => ['default', 'compact', 'bordered'].includes(v),
  },
});

const slots = useSlots();
</script>

<template>
  <div
    :class="[
      'flex flex-col items-center justify-center text-center',
      variant === 'default' && 'py-16 px-6 gap-4',
      variant === 'compact' && 'py-8 px-4 gap-3',
      variant === 'bordered' && 'py-16 px-6 gap-4 border border-dashed border-n-weak os-r-xl bg-n-alpha-2/40',
    ]"
  >
    <!-- Illustration takes priority over icon -->
    <div v-if="slots.illustration" class="mb-1">
      <slot name="illustration" />
    </div>
    <div
      v-else-if="icon || slots.icon"
      :class="[
        'flex items-center justify-center os-r-xl bg-n-brand/[0.08] text-n-blue-text',
        variant === 'compact' ? 'size-10' : 'size-12',
      ]"
    >
      <slot name="icon">
        <Icon :icon="icon" :class="variant === 'compact' ? 'size-5' : 'size-6'" />
      </slot>
    </div>

    <div class="flex flex-col items-center gap-1.5 max-w-sm">
      <h3
        v-if="title || slots.title"
        :class="[
          'm-0 font-semibold text-n-slate-12 tracking-tight',
          variant === 'compact' ? 'text-sm' : 'text-base',
        ]"
      >
        <slot name="title">{{ title }}</slot>
      </h3>
      <p
        v-if="description || slots.description"
        :class="[
          'm-0 text-n-slate-11 leading-relaxed',
          variant === 'compact' ? 'text-xs' : 'text-sm',
        ]"
      >
        <slot name="description">{{ description }}</slot>
      </p>
    </div>

    <div v-if="slots.actions" class="flex items-center gap-2 mt-2">
      <slot name="actions" />
    </div>
  </div>
</template>
